/**
 * Where a stat came from, and the shared state a collection pass needs.
 *
 * The game groups contributions into `StatContext`s and keeps them grouped all the way into
 * the container, because some stats modify a whole context rather than a stat
 * (`IStatCtxModifier` — "increased effect of your auras"). The engine keeps the grouping for
 * the same reason, and because the app will need to answer "where did this 340 armor come
 * from" without recomputing anything.
 */
/**
 * `StatContext.StatCtxType`, minus the ones a build document cannot describe (tools, bonus XP
 * per character).
 */
export const CTX_TYPES = [
    "BASE_STAT",
    // The free elemental resists a character starts with and sheds at 25/50/75. Its own type in
    // the game too, so a breakdown can say "+50 of your fire resist is the newbie grant".
    "NEWBIE_RESISTS",
    "STAT_POINTS",
    "GEAR",
    "TALENT",
    "ASCENDANCY",
    // Spell school perks. `SpellSchoolsData.getStatAndContext` puts them here, and the solo-class
    // bonus deliberately goes to MISC instead so nothing scaling passives can reach it.
    "PASSIVES",
    "ATLAS",
    "JEWEL",
    "AURA",
    "POTION_EFFECT",
    "INNATE_SPELL",
    "SUPPORT_GEM",
    // `mmorpg_stat_compat` over vanilla attributes other mods set, converted into MnS stats. Its
    // own type in the game too (`StatCtxType.VANILLA_STAT_COMPAT`).
    "VANILLA_STAT_COMPAT",
    // The other half of `mmorpg_stat_compat`: vanilla enchantments on equipped gear. A separate
    // type in the game (`GearItemData.getEnchantCompatStats`), and separate here for the same
    // reason — Protection V is something the player put on a chestplate, not an attribute.
    "ENCHANT_COMPAT",
    // Meals, seafood and elixirs (`PlayerBuffData.getStatAndContext`). Temporary, but a character
    // is rarely without one, and `more_food_stats` exists to scale exactly this context.
    "FOOD_BUFF",
    // What `CtxStats.addStatCtxModifierStats` returns: the share of other contexts that
    // `aura_effect` and its two siblings add on top. Never scanned for modifiers itself — the
    // game builds it from the contexts that already exist and appends it afterwards.
    // `mmorpg_sets` — a Diablo-style gear set's tier bonuses. Its own `StatCtxType` in the game
    // too, because the contribution belongs to the *combination* rather than to any one item.
    "ITEM_SET",
    // A map's `Players` affixes. `MapItemData.getStatAndContext` files them under `MOB_AFFIX` for
    // players too — the name is the game's, and it is the type a context modifier would target.
    "MOB_AFFIX",
    "STAT_CTX_MODIFIER_BONUS",
    "MISC",
];
export function makeEnv(snapshot, index, bal, level) {
    const diagnostics = [];
    return {
        snapshot,
        index,
        balance: bal,
        level,
        diagnostics,
        report(severity, code, path, message) {
            diagnostics.push({ severity, code, path, message });
        },
    };
}
export function context(type, source, path, stats) {
    return { type, source, path, stats };
}
//# sourceMappingURL=context.js.map